import { useId, useState, type KeyboardEvent } from 'react';
import { CalendarDays, ChevronLeft, ChevronRight } from 'lucide-react';
import { Input } from './Input';
import { IconButton } from './IconButton';
import { Button } from './Button';
import { Popover } from './Popover';
import { cn } from './cn';
import {
  calendarCells,
  calendarMonth,
  displayDateInput,
  moveCalendarMonth,
  parseDateInput,
  dateRangeError,
} from './fieldData';

function todayIn(timeZone: string): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date());
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  return get('year') + '-' + get('month') + '-' + get('day');
}
const fullDate = (day: string) =>
  new Intl.DateTimeFormat('ru-RU', { dateStyle: 'full', timeZone: 'UTC' }).format(
    new Date(day + 'T12:00:00Z'),
  );
function offsetDay(day: string, delta: number): string | null {
  const date = new Date(day + 'T12:00:00Z');
  date.setUTCDate(date.getUTCDate() + delta);
  const next = date.toISOString().slice(0, 10);
  return parseDateInput(next).day;
}
function Calendar({
  id,
  selected,
  today,
  min,
  max,
  range,
  onSelect,
}: {
  id: string;
  selected: string | null;
  today: string;
  min?: string;
  max?: string;
  range?: readonly [string | null, string | null];
  onSelect: (day: string) => void;
}) {
  const start = selected ?? today;
  const first = min && start < min ? min : max && start > max ? max : start;
  const [cursor, setCursor] = useState(first),
    [month, setMonth] = useState(() => calendarMonth(first)!);
  const available = (day: string) => (!min || day >= min) && (!max || day <= max);
  const focus = (day: string) => {
    if (!available(day)) return;
    setCursor(day);
    setMonth(calendarMonth(day)!);
    requestAnimationFrame(() => document.getElementById(id + '-' + day)?.focus({ preventScroll: true }));
  };
  const shiftMonth = (delta: number) => {
    const next = moveCalendarMonth(cursor, delta);
    if (!next) return;
    const days = calendarCells(next).filter((d): d is string => !!d);
    const day = days[Math.min(Number(cursor.slice(8)) - 1, days.length - 1)];
    const bounded = min && day < min ? min : max && day > max ? max : day;
    focus(bounded);
  };
  const key = (event: KeyboardEvent, day: string) => {
    let next: string | null;
    const weekday = (new Date(day + 'T12:00:00Z').getUTCDay() + 6) % 7;
    if (event.key === 'ArrowLeft') next = offsetDay(day, -1);
    else if (event.key === 'ArrowRight') next = offsetDay(day, 1);
    else if (event.key === 'ArrowUp') next = offsetDay(day, -7);
    else if (event.key === 'ArrowDown') next = offsetDay(day, 7);
    else if (event.key === 'Home') next = offsetDay(day, -weekday);
    else if (event.key === 'End') next = offsetDay(day, 6 - weekday);
    else if (event.key === 'PageUp' || event.key === 'PageDown') {
      event.preventDefault();
      shiftMonth(event.key === 'PageUp' ? -1 : 1);
      return;
    } else return;
    event.preventDefault();
    if (next) focus(next);
  };
  const monthTitle = new Intl.DateTimeFormat('ru-RU', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(month + 'T12:00:00Z'));
  return (
    <div data-slot="calendar" className="p-3">
      <div className="mb-3 flex items-center justify-between gap-2">
        <IconButton
          aria-label="Предыдущий месяц"
          size="sm"
          variant="ghost"
          disabled={
            !moveCalendarMonth(month, -1) ||
            !!(min && moveCalendarMonth(month, -1)! < min.slice(0, 7) + '-01')
          }
          onClick={() => shiftMonth(-1)}
        >
          <ChevronLeft />
        </IconButton>
        <p id={id + '-month'} className="text-sm font-semibold" aria-live="polite">
          {monthTitle.charAt(0).toLocaleUpperCase('ru-RU') + monthTitle.slice(1)}
        </p>
        <IconButton
          aria-label="Следующий месяц"
          size="sm"
          variant="ghost"
          disabled={
            !moveCalendarMonth(month, 1) || !!(max && moveCalendarMonth(month, 1)! > max.slice(0, 7) + '-01')
          }
          onClick={() => shiftMonth(1)}
        >
          <ChevronRight />
        </IconButton>
      </div>
      <div role="grid" aria-labelledby={id + '-month'}>
        <div role="row" className="grid grid-cols-7">
          {['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'].map((d) => (
            <span key={d} role="columnheader" className="pb-2 text-center text-2xs text-fg-subtle">
              {d}
            </span>
          ))}
        </div>
        {Array.from({ length: 6 }, (_, week) => (
          <div key={week} role="row" className="grid grid-cols-7 gap-y-1">
            {calendarCells(month)
              .slice(week * 7, week * 7 + 7)
              .map((day, i) =>
                day ? (
                  <div
                    key={day}
                    role="gridcell"
                    aria-selected={day === selected}
                    className="flex justify-center"
                  >
                    <button
                      id={id + '-' + day}
                      data-calendar-cursor={day === cursor ? '' : undefined}
                      type="button"
                      tabIndex={day === cursor ? 0 : -1}
                      disabled={!available(day)}
                      aria-label={fullDate(day)}
                      aria-current={day === today ? 'date' : undefined}
                      onFocus={() => setCursor(day)}
                      onKeyDown={(e) => key(e, day)}
                      onClick={() => onSelect(day)}
                      className={cn(
                        'flex size-8 items-center justify-center rounded-md text-xs tabular-nums focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none disabled:opacity-40',
                        day === selected
                          ? 'bg-brand-strong font-semibold text-on-brand'
                          : 'text-fg hover:bg-hover',
                        day !== selected &&
                          range?.[0] &&
                          range?.[1] &&
                          day >= range[0] &&
                          day <= range[1] &&
                          'bg-brand-soft text-brand-fg',
                        day === today && day !== selected && 'font-semibold underline underline-offset-4',
                      )}
                    >
                      {Number(day.slice(8))}
                    </button>
                  </div>
                ) : (
                  <span key={'blank-' + i} role="gridcell" />
                ),
              )}
          </div>
        ))}
      </div>
      <div className="mt-3 flex items-center justify-between border-t border-line pt-2">
        <Button
          type="button"
          variant="link"
          size="sm"
          disabled={!available(today)}
          onClick={() => onSelect(today)}
        >
          Сегодня
        </Button>
        <span className="text-2xs text-fg-subtle">← ↑ ↓ → · выбор Enter</span>
      </div>
    </div>
  );
}
export interface DateFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  helper?: string;
  error?: string;
  disabled?: boolean;
  min?: string;
  max?: string;
  timeZone?: string;
  range?: readonly [string | null, string | null];
}
/** Raw text is controlled: partial and invalid input can be retained in a draft. */
export function DateField({
  label,
  value,
  onChange,
  helper,
  error,
  disabled,
  min,
  max,
  timeZone = 'Europe/Moscow',
  range,
}: DateFieldProps) {
  const id = useId(),
    [anchor, setAnchor] = useState<HTMLButtonElement | null>(null),
    [open, setOpen] = useState(false),
    [touched, setTouched] = useState(false);
  const parsed = parseDateInput(value);
  const bounds =
    parsed.day && ((min && parsed.day < min) || (max && parsed.day > max))
      ? 'Дата вне доступного периода.'
      : null;
  const issue = error || (touched ? parsed.error || bounds : undefined);
  return (
    <div data-slot="date-field" className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-xs font-medium text-fg-muted">
        {label}
      </label>
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <Input
            id={id}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onBlur={() => setTouched(true)}
            placeholder="ДД.ММ.ГГГГ"
            inputMode="numeric"
            maxLength={10}
            autoComplete="off"
            disabled={disabled}
            helper={helper}
            error={issue || undefined}
            errorTone="strong"
          />
        </div>
        <IconButton
          ref={setAnchor}
          type="button"
          className="shrink-0"
          variant="secondary"
          aria-label={'Календарь: ' + label}
          aria-expanded={open}
          aria-controls={open ? id + '-popup' : undefined}
          aria-haspopup="dialog"
          disabled={disabled}
          onClick={() => setOpen((v) => !v)}
        >
          <CalendarDays />
        </IconButton>
      </div>
      <Popover
        id={id + '-popup'}
        label={'Календарь: ' + label}
        open={open}
        anchor={anchor}
        onOpenChange={setOpen}
        initialFocus={() =>
          document.querySelector<HTMLElement>('[id="' + id + '-popup"] [data-calendar-cursor]')
        }
      >
        {open && (
          <>
            <Calendar
              id={id + '-calendar'}
              selected={parsed.day}
              today={todayIn(timeZone)}
              min={min}
              max={max}
              range={range}
              onSelect={(day) => {
                onChange(displayDateInput(day));
                setTouched(true);
                setOpen(false);
              }}
            />
            <div className="border-t border-line px-3 py-2">
              <Button
                type="button"
                size="sm"
                variant="link"
                onClick={() => {
                  onChange('');
                  setTouched(true);
                  setOpen(false);
                }}
              >
                Очистить дату
              </Button>
            </div>
          </>
        )}
      </Popover>
    </div>
  );
}
export interface DateRangeValue {
  from: string;
  to: string;
}
export function DateRangeField({
  label,
  value,
  onChange,
  disabled,
}: {
  label: string;
  value: DateRangeValue;
  onChange: (value: DateRangeValue) => void;
  disabled?: boolean;
}) {
  const [touched, setTouched] = useState(false),
    id = useId();
  const issue = touched ? dateRangeError(value.from, value.to) : null;
  const range = [parseDateInput(value.from).day, parseDateInput(value.to).day] as const;
  return (
    <fieldset
      data-slot="date-range-field"
      aria-describedby={id}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node)) setTouched(true);
      }}
      className="min-w-0"
    >
      <legend className="mb-3 text-sm font-semibold">{label}</legend>
      <div className="grid gap-3 sm:grid-cols-2">
        <DateField
          label="Начало"
          value={value.from}
          onChange={(from) => onChange({ ...value, from })}
          disabled={disabled}
          range={range}
        />
        <DateField
          label="Окончание"
          value={value.to}
          onChange={(to) => onChange({ ...value, to })}
          disabled={disabled}
          range={range}
        />
      </div>
      <p
        id={id}
        role={issue ? 'status' : undefined}
        className={cn('mt-2 text-xs', issue ? 'text-danger-strong' : 'text-fg-subtle')}
      >
        {issue || 'Обе даты включены в период. Можно выбрать один день.'}
      </p>
    </fieldset>
  );
}
